/**
 * Reload chat history when a background turn (webhook/cron) finishes on
 * the currently-open session.
 *
 * The shell-level `useSessionEvents` only invalidates `["sessions"]`, which
 * refreshes the sidebar but not the chat body. If a user has the chat tab
 * open while a headless turn halts on a frontend tool (e.g. `question`)
 * in the background, the new events would otherwise stay invisible until
 * F5. We re-fetch `/messages` here (same path as initial page load) instead
 * of replaying the SSE attach buffer — Strands persists messages by the
 * time halt fires, so the REST endpoint already has the new question card.
 * Replaying the buffer would dupe the assistant turn after F5.
 *
 * No-op while a live stream is already running on this tab. Also no-op for
 * a short window after a stream just finished on this tab — the SSE has
 * already delivered the final state, and a reload would only cause a
 * cosmetic flicker (full message-list replace + component remount).
 */

import { useEffect, useRef } from "react";
import { apiFetch } from "@/shared/api";
import { useChatStoreApi } from "../stores/chat-store-context";

interface Options {
  reload: (sessionId: string) => Promise<void> | void;
}

// Window after a tab-local stream ends during which session_changed events
// are ignored. `routes.run` publishes session_changed in its `guarded_stream`
// finally (right after the SSE returns RUN_FINISHED), so without this window
// the just-finished tab would always reload its own chat unnecessarily.
const SELF_FINISH_GRACE_MS = 3_000;

export function useSessionRefresh({ reload }: Options) {
  const storeApi = useChatStoreApi();
  // Timestamp of the most recent streaming → not-streaming transition on
  // this tab. Used to suppress the self-induced session_changed reload.
  const lastStreamEndRef = useRef(0);

  // Watch the store's streaming flag. The store is mutated from multiple
  // places (use-agent-stream's startRun finally, use-session-attach's
  // finally) — subscribing here covers all of them in one spot.
  useEffect(() => {
    let prev = storeApi.getState().streaming;
    return storeApi.subscribe((state) => {
      const next = state.streaming;
      if (prev && !next) {
        lastStreamEndRef.current = Date.now();
      }
      prev = next;
    });
  }, [storeApi]);

  useEffect(() => {
    const ac = new AbortController();
    let stopped = false;

    const run = async () => {
      let backoffMs = 500;
      while (!stopped) {
        try {
          const resp = await apiFetch("/api/events", { signal: ac.signal });
          if (!resp.ok || !resp.body) {
            await new Promise((r) => setTimeout(r, backoffMs));
            backoffMs = Math.min(backoffMs * 2, 10_000);
            continue;
          }
          backoffMs = 500;
          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (!stopped) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split("\n\n");
            buffer = blocks.pop() ?? "";
            for (const block of blocks) {
              for (const line of block.split("\n")) {
                if (!line.startsWith("data: ")) continue;
                try {
                  const evt = JSON.parse(line.slice(6));
                  if (evt?.type !== "session_changed") continue;
                  const s = storeApi.getState();
                  if (
                    evt.sessionId &&
                    evt.sessionId === s.activeSessionId &&
                    !s.streaming &&
                    Date.now() - lastStreamEndRef.current >= SELF_FINISH_GRACE_MS
                  ) {
                    reload(evt.sessionId);
                  }
                } catch {
                  /* skip malformed */
                }
              }
            }
          }
        } catch {
          /* AbortError on unmount, network blip — fall through */
        }
        if (!stopped) {
          await new Promise((r) => setTimeout(r, backoffMs));
          backoffMs = Math.min(backoffMs * 2, 10_000);
        }
      }
    };

    run();

    return () => {
      stopped = true;
      ac.abort();
    };
  }, [storeApi, reload]);
}
